/**
 * Single array of client-scoped workflow manifests (Platform Architecture V2, §5.1).
 * Replaces the hardcoded `sections` array that used to live in `sidebar.tsx`.
 *
 * Order/grouping here IS the sidebar's order/grouping — the sidebar is a pure
 * function of `WORKFLOW_MANIFESTS × useFeatureAccess()`. Agency-level items
 * (`overview`) are NOT here — they aren't per-store gated and stay hardcoded
 * in `sidebar.tsx`.
 *
 * Every `id` below MUST also appear in `feature-ids.json` (the backend registry
 * id snapshot) — enforced by `workflow-manifests.contract.test.ts`. If the
 * backend registry changes, update `feature-ids.json` to match.
 */
import {
  BarChart3,
  Video,
  Megaphone,
  Users,
  ClipboardList,
  LineChart,
  Calendar,
  Settings,
  FileBarChart,
  Link2,
  PackageSearch,
} from "lucide-react";
import type { WorkflowManifest } from "./workflow-manifest";

const STAGE_TEASER = "Available once this client is onboarded";
const GENERIC_CAPABILITY_TEASER = "Connect a platform to unlock";

/**
 * Client workflows, grouped by INTENT and ordered as the client's journey
 * (Plan → Setup → Performance) rather than by data-type. Array order IS the
 * sidebar's order/grouping, so a prospect reads Plan (lit) then Setup/Performance
 * (teased) as a progression — "what unlocks next" — not a scattered padlock wall.
 *
 * Note: the backend `launch` (Launch Scenarios) feature is intentionally NOT in
 * the nav — `scenarios` (Scenario Planner) is the single planning surface. The
 * contract test only requires manifest ids ⊆ backend ids, so omitting it is fine.
 */
export const WORKFLOW_MANIFESTS: WorkflowManifest[] = [
  // ── Plan ── available first (prospect stage); the anchor workflow.
  {
    id: "scenarios",
    label: "Plans",
    icon: LineChart,
    navGroup: "Plan",
    path: "scenarios",
    teaser: { stage: STAGE_TEASER, capability: GENERIC_CAPABILITY_TEASER },
  },
  // ── Performance ── live data surfaces (active stage / connected platforms).
  {
    id: "shop",
    label: "Shop Analytics",
    icon: BarChart3,
    navGroup: "Performance",
    path: "shop",
    connection: { required: ["tiktok_shop"] },
    teaser: {
      stage: STAGE_TEASER,
      capability: "Connect TikTok Shop to unlock",
    },
  },
  {
    id: "products",
    label: "Product Performance",
    icon: PackageSearch,
    navGroup: "Performance",
    path: "products",
    connection: { required: ["tiktok_shop"] },
    teaser: {
      stage: STAGE_TEASER,
      capability: "Connect TikTok Shop to unlock",
    },
  },
  {
    id: "videos",
    label: "Video Performance",
    icon: Video,
    navGroup: "Performance",
    path: "videos",
    connection: { required: ["tiktok_shop"] },
    teaser: {
      stage: STAGE_TEASER,
      capability: "Connect TikTok Shop to unlock",
    },
  },
  {
    id: "ads",
    label: "Ads Management",
    icon: Megaphone,
    navGroup: "Performance",
    path: "ads",
    connection: { required: ["tiktok_business"] },
    teaser: {
      stage: STAGE_TEASER,
      capability: "Connect TikTok Business to unlock",
    },
  },
  {
    id: "creators",
    label: "Creators",
    icon: Users,
    navGroup: "Performance",
    path: "creators",
    connection: { required: ["tiktok_shop"] },
    teaser: {
      stage: STAGE_TEASER,
      capability: "Connect TikTok Shop to unlock",
    },
  },
  {
    id: "reports",
    label: "Reports",
    icon: FileBarChart,
    navGroup: "Performance",
    path: "reports",
    connection: { required: ["tiktok_shop"], optional: ["tiktok_business"] },
    teaser: { stage: STAGE_TEASER, capability: GENERIC_CAPABILITY_TEASER },
  },
  {
    id: "scorecards",
    label: "Scorecards",
    icon: ClipboardList,
    navGroup: "Performance",
    path: "scorecards",
    connection: { required: ["tiktok_shop"], optional: ["tiktok_business"] },
    teaser: { stage: STAGE_TEASER, capability: GENERIC_CAPABILITY_TEASER },
  },
  // ── Setup ── getting the client running (onboarding stage).
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    navGroup: "Setup",
    path: "settings",
    teaser: { stage: STAGE_TEASER, capability: GENERIC_CAPABILITY_TEASER },
  },
  // Staff-only: the view log records who opened a client's shared plan, which is
  // agency-side operational data the client never sees.
  {
    id: "share-links",
    label: "Shared Links",
    icon: Link2,
    navGroup: "Setup",
    path: "share-links",
    teaser: { stage: STAGE_TEASER, capability: GENERIC_CAPABILITY_TEASER },
  },
  {
    id: "calendar",
    label: "Calendar",
    icon: Calendar,
    navGroup: "Setup",
    path: "calendar",
    teaser: { stage: STAGE_TEASER, capability: GENERIC_CAPABILITY_TEASER },
  },
];
